export const LICEO_HOME = "/liceobritanico";

const SCHOOL_ROUTES = [
  "/flashcards",
  "/progress",
  "/podcasts",
  "/tutors",
  "/tutoronboarding",
  "/tutor-studio",
  "/teacherdashboard",
  "/join-class",
  "/subscription",
  "/paywall",
  "/success",
  "/auth",
];

function internalUrl(value: string): URL | null {
  if (!value.startsWith("/") || value.startsWith("//") || /[\\\r\n]/.test(value)) return null;
  try {
    const url = new URL(value, "https://school.invalid");
    return url.origin === "https://school.invalid" ? url : null;
  } catch {
    return null;
  }
}

export function isSchoolPath(value?: string | null): boolean {
  const pathname = value ? internalUrl(value)?.pathname : null;
  return Boolean(
    pathname &&
    (pathname === LICEO_HOME ||
      pathname.startsWith(`${LICEO_HOME}/`) ||
      pathname === "/liceo" ||
      pathname.startsWith("/liceo/"))
  );
}

export function withoutSchoolPrefix(pathname: string): string {
  if (!isSchoolPath(pathname)) return pathname;
  return pathname.replace(/^\/(?:liceobritanico|liceo)(?=\/|$)/, "") || "/";
}

/** Only user-facing routes with school aliases are mapped. APIs and external links stay intact. */
export function schoolHref(href: string, currentPath?: string | null, depth = 0): string {
  if (!isSchoolPath(currentPath)) return href;
  const url = internalUrl(href);
  if (!url) return href;
  const pathname = withoutSchoolPrefix(url.pathname);
  if (
    pathname !== "/" &&
    !SCHOOL_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`))
  )
    return href;
  // The OAuth callback stays on its registered provider URL.
  if (pathname === "/auth/callback") return href;
  url.pathname = `${LICEO_HOME}${pathname === "/" ? "" : pathname}`;
  if (depth < 3) {
    for (const key of ["redirect", "return_to", "redirectedFrom"]) {
      const value = url.searchParams.get(key);
      if (value) url.searchParams.set(key, schoolHref(value, LICEO_HOME, depth + 1));
    }
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

export function schoolAuthReturnTo(
  pathname: string,
  search: string,
  explicit?: string | null
): string | undefined {
  if (!isSchoolPath(pathname)) return explicit || undefined;
  if (explicit) {
    const mapped = schoolHref(explicit, pathname);
    return isSchoolPath(mapped) ? mapped : LICEO_HOME;
  }
  if (withoutSchoolPrefix(pathname).startsWith("/auth")) return LICEO_HOME;
  return schoolHref(`${pathname}${search ? `?${search}` : ""}`, pathname);
}

export function schoolCheckoutReturnTo(pathname: string, returnTo?: string | null): string | null {
  if (isSchoolPath(returnTo)) {
    const normalizedPath = withoutSchoolPrefix(internalUrl(returnTo!)!.pathname);
    if (
      ["/auth", "/paywall", "/success"].some(
        (path) => normalizedPath === path || normalizedPath.startsWith(`${path}/`)
      )
    )
      return LICEO_HOME;
    return schoolHref(returnTo!, LICEO_HOME);
  }
  return isSchoolPath(pathname) ? LICEO_HOME : null;
}
